import { useEffect, useState } from 'react';
import type { McpPolicyIntentGroup } from '../domain/policyTypes';
import { isIrreversibleIntent } from '../domain/intents';

type UseUnselectedPermissionsWarningArgs = {
  /** Reset tracking when the user switches site / workspace. */
  selectedWorkspace: string | null | undefined;
  mcpByIntent: McpPolicyIntentGroup[];
  selectedToolsetNames: ReadonlySet<string>;
};

/**
 * Whether to show the "unselected permissions will limit…" warning.
 *
 * Reversible groups (read / write / search) warn whenever any toolset is off.
 * Irreversible groups (delete / manage) start unchecked by default, so we only
 * warn after the user opts into one and then clears it again.
 */
export function useUnselectedPermissionsWarning({
  selectedWorkspace,
  mcpByIntent,
  selectedToolsetNames,
}: UseUnselectedPermissionsWarningArgs): boolean {
  const [everSelectedIrreversible, setEverSelectedIrreversible] = useState<
    ReadonlySet<string>
  >(() => new Set());

  useEffect(() => {
    setEverSelectedIrreversible(new Set());
  }, [selectedWorkspace]);

  useEffect(() => {
    const irreversibleNames = mcpByIntent
      .filter((group) => isIrreversibleIntent(group.id))
      .flatMap((group) => group.toolsets.map((t) => t.name));
    if (irreversibleNames.length === 0) return;

    setEverSelectedIrreversible((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const name of irreversibleNames) {
        if (selectedToolsetNames.has(name) && !next.has(name)) {
          next.add(name);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [mcpByIntent, selectedToolsetNames]);

  const hasUnselectedReversible = mcpByIntent
    .filter((group) => !isIrreversibleIntent(group.id))
    .flatMap((group) => group.toolsets)
    .some((t) => !selectedToolsetNames.has(t.name));

  const hasDeselectedIrreversible = mcpByIntent
    .filter((group) => isIrreversibleIntent(group.id))
    .flatMap((group) => group.toolsets)
    .some(
      (t) =>
        everSelectedIrreversible.has(t.name) &&
        !selectedToolsetNames.has(t.name),
    );

  return hasUnselectedReversible || hasDeselectedIrreversible;
}
